import Cookies from 'js-cookie';
import { DashboardUserType, parseEnum } from '../enums';
import { SessionCookieNames } from './cookie';
import { AdminSession, SellerSession, Session } from './model';

const ONE_DAY_COOKIE_DURATION = 1;

const setAuthTokenCookie = (name: string, value: string) =>
  Cookies.set(name, value, {
    expires: ONE_DAY_COOKIE_DURATION,
    sameSite: 'strict',
    secure: true,
  });

export type AdminSessionDataArgs = {
  apiV2Token: string;
  name: string;
  lastName: string;
};

export const setAdminSessionData = (args: AdminSessionDataArgs) => {
  setAuthTokenCookie(SessionCookieNames.userType, DashboardUserType.ADMIN);
  setAuthTokenCookie(SessionCookieNames.authApiV2, args.apiV2Token);
  setAuthTokenCookie(SessionCookieNames.userName, args.name);
  setAuthTokenCookie(SessionCookieNames.lastName, args.lastName);
};

export type SellerSessionDataArgs = {
  apiV2Token: string;
  name: string;
  lastName: string;
  businessId: string;
  businessName: string;
};

export const setSellerSessionData = (args: SellerSessionDataArgs) => {
  setAuthTokenCookie(SessionCookieNames.userType, DashboardUserType.SELLER);
  setAuthTokenCookie(SessionCookieNames.authApiV2, args.apiV2Token);
  setAuthTokenCookie(SessionCookieNames.userName, args.name);
  setAuthTokenCookie(SessionCookieNames.lastName, args.lastName);
  setAuthTokenCookie(SessionCookieNames.businessId, args.businessId);
  setAuthTokenCookie(SessionCookieNames.businessName, args.businessName);
};

// Returns:
// - null if no session is stored
// - Session, when a session is retrieved
export const getSessionData = (): Session | null => {
  const userTypeCookieValue = Cookies.get(SessionCookieNames.userType) ?? '';

  const userType = parseEnum(DashboardUserType, userTypeCookieValue);
  if (!userType) return null;

  switch (userType) {
    case DashboardUserType.ADMIN:
      return getAdminSessionData();
    case DashboardUserType.SELLER:
      return getSellerSessionData();
  }
};

function getAdminSessionData(): AdminSession | null {
  const tokenAPIV2 = Cookies.get(SessionCookieNames.authApiV2);
  if (!tokenAPIV2) return null;
  const userName = Cookies.get(SessionCookieNames.userName) ?? '';
  const lastName = Cookies.get(SessionCookieNames.lastName) ?? '';
  return {
    type: DashboardUserType.ADMIN,
    apiV2Token: tokenAPIV2,
    name: userName,
    lastName: lastName,
  };
}

function getSellerSessionData(): SellerSession | null {
  const tokenAPIV2 = Cookies.get(SessionCookieNames.authApiV2);
  if (!tokenAPIV2) return null;
  const userName = Cookies.get(SessionCookieNames.userName) ?? '';
  const lastName = Cookies.get(SessionCookieNames.lastName) ?? '';

  const businessId = Cookies.get(SessionCookieNames.businessId);
  if (!businessId) return null;
  const businessName = Cookies.get(SessionCookieNames.businessName);
  if (!businessName) return null;
  return {
    type: DashboardUserType.SELLER,
    apiV2Token: tokenAPIV2,
    name: userName,
    lastName: lastName,
    businessId,
    businessName,
  };
}

export const clearSession = () => {
  for (const [, value] of Object.entries(SessionCookieNames)) {
    Cookies.remove(value);
  }
};
